/**
 * The postage jobs staff do by hand after a collection is cancelled (Trac #993).
 *
 * One rule for which jobs a cancel leaves. The API worker uses it to ask the
 * backend to record them. The frontend uses it to preview them before staff
 * press Cancel. The kinds are the backend's `CollectionPostageTaskKind` values.
 */

export const POSTAGE_TASK_KINDS = Object.freeze({
  DELETE_FEDEX_SHIPMENT: 'delete_fedex_shipment',
  REFUND_CLICK_AND_DROP: 'refund_click_and_drop',
  CANCEL_FEDEX_COM_PICKUP: 'cancel_fedex_com_pickup',
});

export const POSTAGE_TASK_LABELS = Object.freeze({
  [POSTAGE_TASK_KINDS.DELETE_FEDEX_SHIPMENT]: 'Delete the shipment in FedEx Ship Manager',
  [POSTAGE_TASK_KINDS.REFUND_CLICK_AND_DROP]: 'Refund the label in Royal Mail Click and Drop',
  [POSTAGE_TASK_KINDS.CANCEL_FEDEX_COM_PICKUP]: 'Cancel the pickup on fedex.com',
});

export function postageTaskLabel(kind) {
  return POSTAGE_TASK_LABELS[kind] ?? kind;
}

/**
 * Which hand jobs this booking leaves, from what exists. Pure.
 *   FedEx labels bought              -> delete the shipment in FedEx Ship Manager
 *   Royal Mail shipment created      -> refund it in Click and Drop
 *   FedEx pickup, none booked by us  -> cancel it on fedex.com (never for a drop-off)
 * `royalMailShipments` are rows from the worker's database; the frontend has none.
 */
export function planPostageTasks({ collection, parcels, royalMailShipments, hadPrismPickup }) {
  const kinds = [];
  const fedexCarrier = isCarrier(collection?.carrier, 'fedex') || parcels.some(p => isCarrier(p.carrier, 'fedex'));

  if (fedexLabelNumbers(collection, parcels).length > 0) kinds.push(POSTAGE_TASK_KINDS.DELETE_FEDEX_SHIPMENT);
  if (hasRoyalMailLabel(parcels, royalMailShipments)) kinds.push(POSTAGE_TASK_KINDS.REFUND_CLICK_AND_DROP);
  if (fedexCarrier && !isDropOff(collection) && !hadPrismPickup) kinds.push(POSTAGE_TASK_KINDS.CANCEL_FEDEX_COM_PICKUP);
  return kinds;
}

/** The tracking numbers a staff member needs for one job, for display. */
export function postageTaskTrackingNumbers(kind, { collection, parcels }) {
  if (kind === POSTAGE_TASK_KINDS.REFUND_CLICK_AND_DROP) return labelNumbers(parcels, 'royal');
  return fedexLabelNumbers(collection, parcels);
}

function fedexLabelNumbers(collection, parcels) {
  const numbers = labelNumbers(parcels, 'fedex');
  if (numbers.length === 0 && isCarrier(collection?.carrier, 'fedex') && collection?.tracking_number) {
    numbers.push(collection.tracking_number);
  }
  return numbers;
}

function labelNumbers(parcels, carrierName) {
  const numbers = parcels
    .filter(p => isCarrier(p.carrier, carrierName) && p.tracking_number)
    .map(p => p.tracking_number);
  return [...new Set(numbers)];
}

function hasRoyalMailLabel(parcels, royalMailShipments) {
  const fromParcels = parcels.some(p => isCarrier(p.carrier, 'royal') && p.tracking_number);
  return fromParcels || royalMailShipments.some(s => s.status === 'created');
}

function isCarrier(carrier, name) {
  return String(carrier ?? '').toLowerCase().includes(name);
}

function isDropOff(collection) {
  return ['dropoff', 'drop-off'].includes(String(collection?.send_type ?? '').toLowerCase());
}
